import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useMatch, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, Heart, Instagram, Link2, MapPin, MessageCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
  createClass,
  createProduct,
  createVenue,
  deleteClass,
  deleteProduct,
  deleteVenue,
  fetchSchool,
  fetchSchoolPeople,
  followSchool,
  fetchPaymentQueue,
  fetchSchoolActionItems,
  fetchDoorQueue,
  markInstanceAttendance,
  markInstanceNoShow,
  resolveSchoolActionItem,
  reviewPayment,
  schoolInviteUrl,
  unfollowSchool,
  updateClass,
  updateProduct,
  updateSchool,
  updateVenue,
} from "../lib/schools";
import { formatApiError } from "../lib/api";
import { ReadableCard, ReadableNavButton, navActionClass } from "../components/Sticker";
import CoverColorPicker from "../components/createEvent/CoverColorPicker";
import FormLabel from "../components/ui/FormLabel";
import FormFieldError, { fieldClass } from "../components/ui/FormFieldError";
import VenueInput, { VENUE_NONE, venueIdFromSelect } from "../components/VenueInput";
import {
  CheckboxField,
  ChoiceToggleGroup,
  FieldHint,
  FORM_STYLE_CARD_CLASS,
  SectionTitle,
  choiceToggleClass,
} from "../components/ui/FormSection";
import SchoolPeoplePanel from "../components/schools/SchoolPeoplePanel";
import PersonSheetHost from "../components/schools/PersonSheetHost";
import DoorAttendanceConfirmModal from "../components/schools/DoorAttendanceConfirmModal";
import SchoolOnboardingPanel from "../components/schools/SchoolOnboardingPanel";
import StudioSetupStatusBar from "../components/schools/StudioSetupStatusBar";
import SchoolPublicTeam from "../components/schools/SchoolPublicTeam";
import SelfPaymentDisplay from "../components/SelfPaymentDisplay";
import DangerConfirmModal from "../components/DangerConfirmModal";
import { EmptyState, TabButton } from "../components/ui";
import ActionQueueHeading from "../components/schools/ActionQueueHeading";
import DashboardLoadingSkeleton from "../components/dashboard/DashboardLoadingSkeleton";
import {
  WorkflowModalShell,
  WorkflowModalHeader,
  WorkflowModalBody,
  WorkflowModalFooter,
} from "../components/workflow/WorkflowModal";
import { formatInstagramHandle, instagramHref } from "../lib/instagram";
import { emptyPaymentFields, hasPaymentMethod } from "../lib/payment";
import { formatAmountWithCurrency, parseAmountWithCurrency } from "../lib/moneyInput";
import {
  buildAttendanceWhatsAppMessage,
  buildFeeApprovalMessage,
  buildFeeChaseMessage,
  buildFeeRejectionMessage,
  buildLateCancelResolutionWhatsAppMessage,
  buildNoShowWhatsAppMessage,
  openFeeWhatsApp,
  phoneToWaMe,
  isSchoolStudioTab,
  normalizeSchoolAdminTab,
  resolveSchoolAdminTab,
  SCHOOL_ADMIN_TABS,
  SCHOOL_STUDIO_TABS,
  CANCELLATION_DEADLINE_OPTIONS,
  OFFER_TYPE_OPTIONS,
} from "../lib/schoolFeeMessages";
import WhatsAppFollowUpPrompt from "../components/WhatsAppFollowUpPrompt";
import ProfileThumbnail from "../components/ProfileThumbnail";
import PublicListingsBody from "../components/profile/PublicListingUi";
import { normalizeListingEvents } from "../lib/publicListingFormat";
import LinkifiedText from "../components/LinkifiedText";
import { backLabel, defaultBackLabel, defaultBackPath, goBack } from "../lib/navBack";
import HostThemedShell from "../components/ui/public/HostThemedShell";
import { DEFAULT_HOST_THEME, normalizeHostTheme } from "../lib/hostThemes";

const TABS = SCHOOL_ADMIN_TABS;
const DEFAULT_STUDIO_TAB = "classes";
/** Contact / follow links on the public school header. */
const SCHOOL_PUBLIC_HEADER_LINK_CLASS =
  "inline-flex items-center gap-1.5 min-h-[44px] font-ui font-medium text-[13px] text-ink/55 hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm";
const SCHOOL_PUBLIC_BIO_LINK_CLASS =
  "underline underline-offset-2 text-ink/70 hover:text-ink transition-colors";
const DAYS = [
  { k: "MON", label: "Mon" },
  { k: "TUE", label: "Tue" },
  { k: "WED", label: "Wed" },
  { k: "THU", label: "Thu" },
  { k: "FRI", label: "Fri" },
  { k: "SAT", label: "Sat" },
  { k: "SUN", label: "Sun" },
];
const PRODUCT_TYPES = [
  { k: "DROPIN", label: "Drop-in" },
  { k: "PACK", label: "Class pack" },
  { k: "MEMBERSHIP", label: "Membership" },
  { k: "OFFER", label: "Offer" },
];

const PRODUCT_TYPE_LABELS = Object.fromEntries(PRODUCT_TYPES.map((t) => [t.k, t.label]));

function dayToggleClass(active) {
  return choiceToggleClass(active);
}

function formatDt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatClock(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatSessionWhen(inst) {
  const start = inst.effective_datetime || inst.datetime;
  const end = inst.effective_end_datetime;
  if (!start) return "";
  const dayPart = new Date(start).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  if (!end) return `${dayPart} · ${formatClock(start)}`;
  return `${dayPart} · ${formatClock(start)} – ${formatClock(end)}`;
}

function endTimeLabel(startHHMM, durationMinutes) {
  const [h, m] = String(startHHMM || "19:00").split(":").map(Number);
  const total = (h || 0) * 60 + (m || 0) + (Number(durationMinutes) || 60);
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  const d = new Date();
  d.setHours(eh, em, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatRecurrence(recurrence = {}) {
  const days = (recurrence.days || []).join(", ");
  const time = recurrence.time || "";
  const duration = recurrence.duration_minutes;
  if (!days && !time) return "Schedule not set";
  const timePart = time && duration
    ? `${time} – ${endTimeLabel(time, duration)}`
    : time;
  return [days, timePart].filter(Boolean).join(" · ");
}

const DURATION_OPTIONS = [
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hour" },
  { value: 75, label: "1 hr 15" },
  { value: 90, label: "1 hr 30" },
  { value: 120, label: "2 hours" },
];

function DurationField({ value, onChange, startTime, testId }) {
  const mins = Number(value) || 60;
  return (
    <label className="block">
      <FormLabel className="block mb-2">Duration *</FormLabel>
      <select
        className="field"
        value={mins}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
      >
        {DURATION_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        {!DURATION_OPTIONS.some((o) => o.value === mins) && (
          <option value={mins}>{mins} min</option>
        )}
      </select>
      {startTime ? (
        <FieldHint className="mt-1.5">
          Ends at {endTimeLabel(startTime, mins)}
        </FieldHint>
      ) : null}
    </label>
  );
}

export default function SchoolPage() {
  const { slug } = useParams();
  const adminRoute = Boolean(useMatch("/admin/:slug"));
  const [params, setSearchParams] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const { account } = useAuth();

  const tabParamRaw = params.get("tab");
  const tabParam = normalizeSchoolAdminTab(tabParamRaw);
  const [tab, setTab] = useState(() => (TABS.includes(tabParam) ? tabParam : "classes"));
  const lastStudioTabRef = useRef(
    TABS.includes(tabParam) && isSchoolStudioTab(tabParam) ? tabParam : DEFAULT_STUDIO_TAB,
  );

  const [school, setSchool] = useState(null);
  const [products, setProducts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [venues, setVenues] = useState([]);
  const [team, setTeam] = useState([]);
  const [calendar, setCalendar] = useState({ instances: [], events: [], class_listings: [] });
  const [people, setPeople] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const followAfterAuthRef = useRef(false);
  const [pendingFeeCount, setPendingFeeCount] = useState(0);
  const showAdmin = adminRoute && isAdmin;

  async function refreshSchool(idOrSlug = slug) {
    const data = await fetchSchool(idOrSlug, {
      includeCalendar: true,
      // Admin studio Classes tab previews upcoming instances; public page uses listings only.
      includeInstances: Boolean(adminRoute),
    });
    setSchool(data.school);
    setProducts(data.products || []);
    setClasses(data.classes || []);
    setVenues(data.venues || []);
    setTeam(data.team || []);
    setIsAdmin(Boolean(data.is_admin));
    setFollowing(Boolean(data.is_following));
    setCalendar(data.calendar || { instances: [], events: [], class_listings: [] });
    return data;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await refreshSchool(slug);
        if (cancelled) return;
        if (adminRoute && data.is_admin) {
          const [peopleData, pending, actionItemsData, doorData] = await Promise.all([
            fetchSchoolPeople(data.school.id),
            fetchPaymentQueue(data.school.id, "PENDING").catch(() => []),
            fetchSchoolActionItems(data.school.id, "OPEN").catch(() => ({ action_items: [], open_count: 0 })),
            fetchDoorQueue(data.school.id).catch(() => ({ items: [], door_count: 0 })),
          ]);
          if (cancelled) return;
          setPeople(peopleData);
          const count = (pending || []).length
            + Number(actionItemsData?.open_count || 0)
            + Number(doorData?.door_count || 0);
          setPendingFeeCount(count);
          if (!tabParamRaw) {
            const next = resolveSchoolAdminTab({
              tabParam: null,
              pendingCount: count,
              tabs: TABS,
              fallback: "classes",
            });
            if (next !== tab) {
              setTab(next);
              setSearchParams((prev) => {
                const nextParams = new URLSearchParams(prev);
                nextParams.set("tab", next);
                return nextParams;
              }, { replace: true });
            }
          } else if (tabParamRaw === "fees") {
            setTab("action-items");
            setSearchParams((prev) => {
              const nextParams = new URLSearchParams(prev);
              nextParams.set("tab", "action-items");
              return nextParams;
            }, { replace: true });
          }
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, adminRoute]);

  useEffect(() => {
    if (!school?.name) return undefined;
    const previousTitle = document.title;
    document.title = `${school.name} — rsvp.dance`;
    return () => {
      document.title = previousTitle;
    };
  }, [school?.name]);

  useEffect(() => {
    if (TABS.includes(tabParam) && tabParam !== tab) setTab(tabParam);
  }, [tabParam]);

  useEffect(() => {
    if (isSchoolStudioTab(tab)) lastStudioTabRef.current = tab;
  }, [tab]);

  function resolveTab(next) {
    if (next === "studio") {
      const preferred = lastStudioTabRef.current;
      return SCHOOL_STUDIO_TABS.includes(preferred) ? preferred : DEFAULT_STUDIO_TAB;
    }
    return next;
  }

  function switchTab(next) {
    const resolved = resolveTab(next);
    setTab(resolved);
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set("tab", resolved);
      return nextParams;
    }, { replace: true });
  }

  function handleSchoolTabKeyDown(e) {
    const navKeys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!navKeys.includes(e.key)) return;

    const tabs = [...e.currentTarget.querySelectorAll('[role="tab"]')];
    if (tabs.length === 0) return;

    const currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1 && e.key !== "Home" && e.key !== "End") return;

    e.preventDefault();

    let nextIndex = currentIndex;
    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = tabs.length - 1;
    }

    const nextTab = tabs[nextIndex];
    const tabKey = nextTab?.dataset?.tabKey;
    if (!tabKey) return;

    nextTab.focus();
    switchTab(tabKey);
  }

  function switchTabAndScroll(next) {
    switchTab(next);
    window.requestAnimationFrame(() => {
      document.querySelector("[data-testid='school-tabs']")?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }

  async function copyInvite() {
    if (!school) return;
    try {
      await navigator.clipboard.writeText(schoolInviteUrl(school.slug));
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy link");
    }
  }

  async function toggleFollow() {
    if (!school || followBusy) return;
    if (!account) {
      const schoolPath = school.slug || slug;
      nav(`/login?next=${encodeURIComponent(`/school/${schoolPath}?follow=1`)}`);
      return;
    }
    setFollowBusy(true);
    try {
      if (following) {
        await unfollowSchool(school.id);
        setFollowing(false);
        toast.success("Unfollowed — you won't get school updates.");
      } else {
        await followSchool(school.id);
        setFollowing(true);
        toast.success(`Following — you'll hear when ${school.name} posts nights.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFollowBusy(false);
    }
  }

  useEffect(() => {
    if (!account || !school || loading) return;
    if (params.get("follow") !== "1") return;
    if (followAfterAuthRef.current) return;
    followAfterAuthRef.current = true;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("follow");
        return next;
      },
      { replace: true },
    );

    if (following) {
      toast.message(`You're already following ${school.name}.`);
      return undefined;
    }

    const schoolId = school.id;
    const schoolName = school.name;
    let cancelled = false;

    (async () => {
      setFollowBusy(true);
      try {
        await followSchool(schoolId);
        if (cancelled) return;
        setFollowing(true);
        toast.success(`Following ${schoolName} — you'll hear when they post nights.`, {
          duration: 8000,
          action: {
            label: "Undo",
            onClick: () => {
              (async () => {
                try {
                  await unfollowSchool(schoolId);
                  setFollowing(false);
                  toast.success("Unfollowed — you won't get school updates.");
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              })();
            },
          },
        });
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiError(e), {
            description: "You're signed in — tap Follow on the school page to try again.",
          });
        }
      } finally {
        if (!cancelled) setFollowBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [account, school, loading, following, params, setSearchParams]);

  async function reloadPeople() {
    if (!school) return;
    const data = await fetchSchoolPeople(school.id);
    setPeople(data);
  }

  if (loading) {
    return <DashboardLoadingSkeleton label="Loading school…" />;
  }

  if (!school) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-8 text-center">
        <p className="font-ui font-semibold text-[18px] text-ink">School not found.</p>
        <FieldHint className="mt-2 max-w-sm">
          This link may be outdated, or the school hasn&apos;t published a public page yet.
        </FieldHint>
        <ReadableNavButton
          as={Link}
          to="/events"
          className="mt-5 px-5"
          data-testid="school-not-found-events"
        >
          Browse events
        </ReadableNavButton>
      </div>
    );
  }

  if (adminRoute && !isAdmin) {
    return <Navigate to={`/school/${slug}`} replace />;
  }

  const ownerTheme = normalizeHostTheme(school?.theme || DEFAULT_HOST_THEME);
  const pageClassName = `min-h-screen font-body text-ink bg-canvas ${showAdmin ? "pb-24" : "pb-16"}`;
  const Shell = showAdmin ? "div" : HostThemedShell;
  const shellProps = showAdmin
    ? { className: pageClassName }
    : { theme: ownerTheme, className: pageClassName };

  return (
    <Shell {...shellProps}>
      <div className="max-w-page mx-auto px-4 pt-5 pb-2 flex items-center justify-between gap-2 flex-wrap">
        <ReadableNavButton
          type="button"
          onClick={() =>
            goBack(nav, location, [
              showAdmin ? "/me?tab=host&section=school" : null,
              defaultBackPath(account),
            ])
          }
          className={navActionClass}
          data-testid="school-back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          {backLabel(location, showAdmin ? "Host profile" : defaultBackLabel(account))}
        </ReadableNavButton>
        {showAdmin && (
          <div className="flex items-center gap-2">
            <ReadableNavButton
              type="button"
              variant="primary"
              onClick={copyInvite}
              className="inline-flex items-center gap-1.5"
              data-testid="school-copy-invite"
            >
              <Link2 size={14} aria-hidden />
              Copy invite link
            </ReadableNavButton>
            <ReadableNavButton
              as={Link}
              to={`/school/${school.slug || slug}`}
              className={`shrink-0 ${navActionClass}`}
              data-testid="school-view-public"
              aria-label="View public page"
              title="View public page"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            </ReadableNavButton>
          </div>
        )}
      </div>

      {showAdmin ? (
        <>
          <header className="max-w-page mx-auto px-4 mt-3">
            <h1 className="font-ui font-bold text-[28px] leading-tight text-balance">
              {school.name}
            </h1>
            {school.description && (
              <FieldHint className="mt-1.5 text-[14px] whitespace-pre-line">
                <LinkifiedText>{school.description}</LinkifiedText>
              </FieldHint>
            )}
            {school.whatsapp_url && (
              <div className="mt-4">
                <ReadableNavButton
                  as="a"
                  href={school.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5"
                  data-testid="school-whatsapp-link"
                >
                  <MessageCircle size={14} aria-hidden />
                  WhatsApp community
                </ReadableNavButton>
              </div>
            )}
          </header>

          <div className="max-w-page mx-auto px-4 mt-5">
            <SchoolOnboardingPanel
              school={school}
              venues={venues}
              classes={classes}
              products={products}
              onSchoolUpdated={(next) => setSchool(next)}
              onSwitchTab={switchTabAndScroll}
              onCopyInvite={copyInvite}
              onPeopleRefresh={reloadPeople}
            />
          </div>

          <div className="max-w-page mx-auto px-4 mt-6">
            <div
              className="inline-flex gap-2 flex-wrap"
              role="tablist"
              aria-label="School sections"
              data-testid="school-tabs"
              onKeyDown={handleSchoolTabKeyDown}
            >
              <TabButton
                tabKey="action-items"
                active={tab === "action-items"}
                onClick={() => switchTab("action-items")}
                testid="school-tab-action-items"
                panelId="school-panel-action-items"
                badge={pendingFeeCount > 0 ? pendingFeeCount : null}
                aria-label={pendingFeeCount > 0 ? `Action Items, ${pendingFeeCount} pending` : "Action Items"}
              >
                Action Items
              </TabButton>
              <TabButton
                tabKey="people"
                active={tab === "people"}
                onClick={() => switchTab("people")}
                testid="school-tab-people"
                panelId="school-panel-people"
              >
                People
              </TabButton>
              <TabButton
                tabKey="studio"
                active={isSchoolStudioTab(tab)}
                onClick={() => switchTab("studio")}
                testid="school-tab-studio"
                panelId="school-panel-studio"
              >
                Studio
              </TabButton>
            </div>
          </div>

          <div className="max-w-page mx-auto px-4 mt-7 space-y-6">
            {isSchoolStudioTab(tab) && (
              <div
                role="tabpanel"
                id="school-panel-studio"
                aria-labelledby="school-tab-studio"
                className="space-y-6"
              >
                <div className="space-y-3">
                  <div
                    className="inline-flex gap-2 flex-wrap"
                    role="tablist"
                    aria-label="Studio sections"
                    data-testid="school-studio-tabs"
                    onKeyDown={handleSchoolTabKeyDown}
                  >
                    <TabButton
                      tabKey="profile"
                      active={tab === "profile"}
                      onClick={() => switchTab("profile")}
                      testid="school-tab-profile"
                      panelId="school-panel-profile"
                    >
                      Profile
                    </TabButton>
                    <TabButton
                      tabKey="venues"
                      active={tab === "venues"}
                      onClick={() => switchTab("venues")}
                      testid="school-tab-venues"
                      panelId="school-panel-venues"
                    >
                      Venues
                    </TabButton>
                    <TabButton
                      tabKey="classes"
                      active={tab === "classes"}
                      onClick={() => switchTab("classes")}
                      testid="school-tab-classes"
                      panelId="school-panel-classes"
                    >
                      Classes
                    </TabButton>
                    <TabButton
                      tabKey="products"
                      active={tab === "products"}
                      onClick={() => switchTab("products")}
                      testid="school-tab-products"
                      panelId="school-panel-products"
                    >
                      Products
                    </TabButton>
                  </div>

                  <StudioSetupStatusBar
                    school={school}
                    venues={venues}
                    classes={classes}
                    products={products}
                    onSchoolUpdated={(next) => setSchool(next)}
                    onSwitchTab={switchTabAndScroll}
                  />
                </div>

                {tab === "classes" && (
                  <div role="tabpanel" id="school-panel-classes" aria-labelledby="school-tab-classes" className="scroll-mt-4">
                    <ClassesTab
                      school={school}
                      classes={classes}
                      venues={venues}
                      team={people?.team || []}
                      calendar={calendar}
                      onCreated={async () => {
                        await refreshSchool(school.id);
                      }}
                    />
                  </div>
                )}
                {tab === "venues" && (
                  <div role="tabpanel" id="school-panel-venues" aria-labelledby="school-tab-venues" className="scroll-mt-4">
                    <VenuesTab
                      school={school}
                      venues={venues}
                      onChanged={async () => {
                        await refreshSchool(school.id);
                      }}
                    />
                  </div>
                )}
                {tab === "profile" && (
                  <div role="tabpanel" id="school-panel-profile" aria-labelledby="school-tab-profile" className="scroll-mt-4">
                    <SchoolProfileTab school={school} />
                  </div>
                )}
                {tab === "products" && (
                  <div role="tabpanel" id="school-panel-products" aria-labelledby="school-tab-products" className="scroll-mt-4">
                    <ProductsTab
                      school={school}
                      products={products}
                      classes={classes}
                      onCreated={async () => {
                        await refreshSchool(school.id);
                      }}
                      onSchoolUpdated={async () => {
                        await refreshSchool(school.id);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {tab === "people" && (
              <div role="tabpanel" id="school-panel-people" aria-labelledby="school-tab-people" className="scroll-mt-4">
                <ReadableCard className={FORM_STYLE_CARD_CLASS}>
                  <FieldHint>
                    Wrong fee amount? Open the person and Adjust credits. Tap someone to message, add a note, or change their role in the dropdown.
                  </FieldHint>
                  {people ? (
                    <SchoolPeoplePanel
                      schoolId={school.id}
                      schoolName={school.name}
                      team={people.team}
                      people={people.people}
                      onRefresh={reloadPeople}
                    />
                  ) : (
                    <p className="text-sm text-ink/60 pt-2">Loading people…</p>
                  )}
                </ReadableCard>
              </div>
            )}
            {tab === "action-items" && (
              <div role="tabpanel" id="school-panel-action-items" aria-labelledby="school-tab-action-items" className="scroll-mt-4">
                <ActionItemsTab
                  school={school}
                  people={people}
                  onRefreshPeople={reloadPeople}
                  onPendingCountChange={setPendingFeeCount}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <PublicSchoolView
          school={school}
          calendar={calendar}
          venues={venues}
          team={team}
          following={following}
          followBusy={followBusy}
          onToggleFollow={toggleFollow}
        />
      )}
    </Shell>
  );
}

function ProfileDisplayField({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <p className="font-ui text-[11px] font-medium text-ink/70 mb-1">{label}</p>
      <div className="font-ui text-[14px] font-medium text-ink break-words">
        {children || <span className="text-ink/45 font-normal">Not set</span>}
      </div>
    </div>
  );
}

function ProfileEditLink({ to, testId }) {
  return (
    <div className="space-y-3 pt-1">
      <FieldHint>
        This can currently be edited from your main profile. We keep forms in one place.
      </FieldHint>
      <ReadableNavButton as={Link} to={to} variant="primary" data-testid={testId}>
        <Pencil size={16} strokeWidth={2.5} aria-hidden />
        Edit
      </ReadableNavButton>
    </div>
  );
}

function SchoolProfileTab({ school }) {
  const { account } = useAuth();
  const payment = emptyPaymentFields(account);
  const igLabel = formatInstagramHandle(school.instagram);
  const igHref = instagramHref(school.instagram);
  const whatsappUrl = (school.whatsapp_url || "").trim();

  return (
    <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-profile-tab">
      <div className="space-y-6">
        <section className="space-y-4" aria-label="School profile">
          <div>
            <SectionTitle>School profile</SectionTitle>
            <FieldHint className="mt-1">
              Name, city, bio, Instagram, and WhatsApp link.
            </FieldHint>
          </div>
          <div className="space-y-4">
            <ProfileDisplayField label="School name" testId="school-profile-name">
              {school.name}
            </ProfileDisplayField>
            <ProfileDisplayField label="School city" testId="school-profile-city">
              {school.city}
            </ProfileDisplayField>
            <ProfileDisplayField label="Short bio" testId="school-profile-bio">
              {school.description ? (
                <p className="whitespace-pre-line leading-relaxed font-normal">
                  <LinkifiedText>{school.description}</LinkifiedText>
                </p>
              ) : null}
            </ProfileDisplayField>
            <ProfileDisplayField label="School Instagram" testId="school-profile-instagram">
              {igLabel && igHref ? (
                <a
                  href={igHref}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-plum hover:underline"
                >
                  {igLabel}
                </a>
              ) : null}
            </ProfileDisplayField>
            <ProfileDisplayField label="WhatsApp community link" testId="school-profile-whatsapp">
              {whatsappUrl ? (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-plum hover:underline break-all"
                >
                  {whatsappUrl}
                </a>
              ) : null}
            </ProfileDisplayField>
          </div>
          <ProfileEditLink to="/me?tab=host&section=school" testId="school-profile-edit" />
        </section>

        <section className="space-y-4 pt-5 border-t border-black/[0.08]" aria-label="Payment Methods">
          <div>
            <SectionTitle>Payment Methods</SectionTitle>
            <FieldHint className="mt-1">
              Payments for class products use your host default payment method.
            </FieldHint>
          </div>
          {hasPaymentMethod(payment) ? (
            <SelfPaymentDisplay
              selfPayment={payment}
              payeeName={account?.name || school.name}
              variant="profile"
              idPrefix="school-payment"
            />
          ) : (
            <div
              className="rounded-md2 border border-black/[0.08] bg-cream/60 px-4 py-4"
              data-testid="school-payment-empty"
            >
              <p className="font-ui font-medium text-[14px] text-ink">No payment details yet.</p>
              <p className="font-ui text-[13px] text-ink/75 leading-relaxed mt-1">
                Add a UPI ID or payment link in your host profile so students know how to pay.
              </p>
            </div>
          )}
          <ProfileEditLink to="/me?tab=host&section=payment" testId="school-payment-edit" />
        </section>
      </div>
    </ReadableCard>
  );
}

function PublicSchoolView({
  school,
  calendar,
  venues = [],
  team = [],
  following = false,
  followBusy = false,
  onToggleFollow,
}) {
  const upcomingClasses = calendar.class_listings?.length
    ? calendar.class_listings
    : [];
  const upcomingEvents = normalizeListingEvents(calendar.events || []);
  const igHref = instagramHref(school.instagram);
  const igLabel = formatInstagramHandle(school.instagram);
  const whatsappUrl = (school.whatsapp_url || "").trim();
  const [heartPulse, setHeartPulse] = useState(false);
  const wasFollowingRef = useRef(following);

  useEffect(() => {
    if (following && !wasFollowingRef.current) {
      setHeartPulse(true);
    }
    wasFollowingRef.current = following;
  }, [following]);

  const headerLeadWa = (() => {
    if (whatsappUrl) return null;
    const lead = (team || []).find(
      (p) => p.role === "ADMIN" && phoneToWaMe(p.phone),
    );
    if (!lead) return null;
    const firstName = String(lead.name || "").split(/\s+/)[0] || "school";
    return {
      id: lead.id,
      href: phoneToWaMe(lead.phone),
      label: `Message ${firstName}`,
      aria: `Message ${lead.name} on WhatsApp about classes`,
    };
  })();

  const classesHint =
    school.commerce_enabled === false ? (
      <FieldHint className="mt-1">
        Pay this school directly — the app only tracks who&apos;s coming to class.
      </FieldHint>
    ) : null;

  const externalHint = " (opens in a new tab)";

  return (
    <div className="max-w-page mx-auto px-4 mt-6 space-y-6 pb-10">
      <ReadableCard
        className="p-5 sm:p-6"
        data-testid="school-profile-header"
      >
        <div className="flex items-start gap-4">
          <ProfileThumbnail
            name={school.name}
            size="lg"
            className="bg-canvas ring-1 ring-black/[0.06]"
          />
          <div className="min-w-0 flex-1">
            <h1
              className="font-ui font-semibold text-[24px] sm:text-[28px] leading-[1.1] tracking-tight text-balance text-ink"
              data-testid="school-profile-public-name"
            >
              {school.name}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0">
              {school.city && (
                <span className="font-ui font-medium text-[13px] text-ink/55">
                  {school.city}
                </span>
              )}
              {igHref && igLabel && (
                <a
                  href={igHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Instagram: ${igLabel}${externalHint}`}
                  className={SCHOOL_PUBLIC_HEADER_LINK_CLASS}
                  data-testid="school-public-instagram"
                >
                  <Instagram className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                  {igLabel}
                </a>
              )}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${school.name} WhatsApp group${externalHint}`}
                  className={SCHOOL_PUBLIC_HEADER_LINK_CLASS}
                  data-testid="school-public-whatsapp"
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                  WhatsApp group
                </a>
              )}
              {!whatsappUrl && headerLeadWa && (
                <a
                  href={headerLeadWa.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${headerLeadWa.aria}${externalHint}`}
                  className={SCHOOL_PUBLIC_HEADER_LINK_CLASS}
                  data-testid="school-public-whatsapp"
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                  {headerLeadWa.label}
                </a>
              )}
              <button
                type="button"
                onClick={onToggleFollow}
                disabled={followBusy}
                aria-busy={followBusy || undefined}
                aria-label={
                  followBusy
                    ? following
                      ? `Unfollowing ${school.name}`
                      : `Following ${school.name}`
                    : following
                      ? `Following ${school.name}`
                      : `Follow ${school.name}`
                }
                className={`${SCHOOL_PUBLIC_HEADER_LINK_CLASS} disabled:opacity-50`}
                aria-pressed={following}
                data-testid={following ? "school-unfollow-btn" : "school-follow-btn"}
              >
                <Heart
                  className={[
                    "w-3.5 h-3.5",
                    following ? "fill-ink text-ink" : undefined,
                    heartPulse ? "follow-heart-pop" : undefined,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  strokeWidth={2}
                  aria-hidden
                  onAnimationEnd={() => setHeartPulse(false)}
                />
                {followBusy ? "…" : following ? "Following" : "Follow"}
              </button>
            </div>
          </div>
        </div>

        {school.description && (
          <p className="mt-5 pt-4 border-t border-black/[0.06] font-ui text-[14px] font-normal text-ink/70 leading-relaxed text-pretty whitespace-pre-line">
            <LinkifiedText linkClassName={SCHOOL_PUBLIC_BIO_LINK_CLASS}>
              {school.description}
            </LinkifiedText>
          </p>
        )}
      </ReadableCard>

      <PublicListingsBody
        classes={upcomingClasses}
        events={upcomingEvents}
        venues={venues}
        headerSchoolName={school.name}
        emptyName={school.name}
        sectionOrder="school"
        testIdPrefix="school-profile"
        classesHint={classesHint}
        drawerLinks
        afterClasses={
          <SchoolPublicTeam
            team={team}
            hideWhatsAppForIds={headerLeadWa?.id ? [headerLeadWa.id] : []}
          />
        }
      />
    </div>
  );
}

function VenueCatalogForm({
  initialVenue,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  onDelete,
}) {
  const [draft, setDraft] = useState(() => ({
    name: initialVenue?.name || "",
    address_or_link: initialVenue?.address_or_link || "",
  }));
  const [errors, setErrors] = useState({});

  function update(patch) {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  }

  function openMapSearch() {
    const query = draft.address_or_link.trim() || draft.name.trim();
    if (!query) {
      toast.error("Add an address before opening Maps.");
      return;
    }
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = {};
    if (!draft.name.trim()) nextErrors.name = "Enter a venue name.";
    if (!draft.address_or_link.trim()) {
      nextErrors.address_or_link = "Add an address or map link.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSubmit({
      name: draft.name.trim(),
      address_or_link: draft.address_or_link.trim(),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="block">
        <FormLabel className="block mb-2">Venue name *</FormLabel>
        <input
          className={fieldClass(errors.name)}
          value={draft.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Studio A"
          autoFocus={Boolean(initialVenue)}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? "school-venue-name-error" : undefined}
          data-testid="school-venue-name"
        />
        {errors.name ? (
          <FormFieldError id="school-venue-name-error">{errors.name}</FormFieldError>
        ) : (
          <FieldHint className="mt-1.5">
            Use a short name students will recognize, such as “Studio A.”
          </FieldHint>
        )}
      </label>
      <div>
        <FormLabel className="block mb-2">Address or map link *</FormLabel>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <input
            className={`${fieldClass(errors.address_or_link)} flex-1 min-w-0`}
            value={draft.address_or_link}
            onChange={(e) => update({ address_or_link: e.target.value })}
            placeholder="Prenzlauer Berg, Berlin"
            aria-invalid={Boolean(errors.address_or_link)}
            aria-describedby={errors.address_or_link ? "school-venue-address-error" : undefined}
            data-testid="school-venue-address"
          />
          <ReadableNavButton
            type="button"
            onClick={openMapSearch}
            disabled={!draft.address_or_link.trim() && !draft.name.trim()}
            data-testid="school-venue-map-search"
          >
            <MapPin className="w-4 h-4" strokeWidth={2} aria-hidden /> Maps
          </ReadableNavButton>
        </div>
        {errors.address_or_link ? (
          <FormFieldError id="school-venue-address-error">{errors.address_or_link}</FormFieldError>
        ) : (
          <FieldHint className="mt-1.5">
            Paste a street address or Google Maps link.
          </FieldHint>
        )}
      </div>
      <div className={`flex flex-wrap items-center gap-2 pt-1 ${onDelete ? "justify-between" : "justify-end"}`}>
        {onDelete && (
          <ReadableNavButton
            type="button"
            variant="dangerOutline"
            onClick={onDelete}
            className="inline-flex items-center gap-1.5"
          >
            <Trash2 size={15} aria-hidden /> Delete venue
          </ReadableNavButton>
        )}
        <div className="flex gap-2">
          <ReadableNavButton type="button" onClick={onCancel}>Cancel</ReadableNavButton>
          <ReadableNavButton type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving…" : submitLabel}
          </ReadableNavButton>
        </div>
      </div>
    </form>
  );
}

function VenuesTab({ school, venues, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const formOpen = showForm || Boolean(editing);

  async function handleCreate(payload) {
    setBusy(true);
    try {
      await createVenue(school.id, payload);
      toast.success("Venue added");
      setShowForm(false);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(venue) {
    setShowForm(false);
    setEditing({
      id: venue.id,
      name: venue.name,
      address_or_link: venue.address_or_link,
    });
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function handleUpdate(payload) {
    setBusy(true);
    try {
      await updateVenue(school.id, editing.id, payload);
      toast.success("Venue updated");
      setEditing(null);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await deleteVenue(school.id, deleteTarget.id);
      toast.success("Venue deleted");
      setDeleteTarget(null);
      setEditing(null);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <ReadableCard className={FORM_STYLE_CARD_CLASS}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <SectionTitle>Venues</SectionTitle>
          <FieldHint>
            Studios where you teach. Pick one when you schedule a class.
          </FieldHint>
        </div>
        {!formOpen && (
          <ReadableNavButton
            type="button"
            variant="primary"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            data-testid="school-add-venue"
            aria-label="Add venue"
          >
            <Plus size={16} strokeWidth={2.5} aria-hidden />
            Venue
          </ReadableNavButton>
        )}
      </div>

      {formOpen && (
        <div className="pt-4 mt-2 border-t border-black/[0.08]">
          <VenueCatalogForm
            key={editing?.id || "new"}
            initialVenue={editing || undefined}
            busy={busy}
            submitLabel={editing ? "Save changes" : "Save venue"}
            onSubmit={editing ? handleUpdate : handleCreate}
            onCancel={closeForm}
            onDelete={editing ? () => setDeleteTarget(venues.find((v) => v.id === editing.id) || editing) : undefined}
          />
        </div>
      )}

      {venues.length ? (
        <ul className="pt-2 divide-y divide-black/[0.08]">
          {venues.map((v) => (
            <li
              key={v.id}
              className="py-3 first:pt-2"
              data-testid={`school-venue-${v.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-ui font-semibold text-[14px]">{v.name}</div>
                  <div className="text-[13px] text-ink/70 mt-0.5 truncate" title={v.address_or_link}>
                    {v.address_or_link}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => startEdit(v)}
                  className="inline-flex min-h-[44px] items-center gap-1.5 px-1 text-sm font-semibold text-plum shrink-0"
                  data-testid={`school-venue-edit-${v.id}`}
                >
                  <Pencil size={14} aria-hidden /> Edit
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        !formOpen && (
          <FieldHint className="pt-2">
            No venues yet. Tap + Venue to add a studio.
          </FieldHint>
        )
      )}
      <DangerConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete venue?"
        message="Classes associated with this venue will no longer have a venue. They’ll default to “ask the host” until you choose another venue."
        confirmLabel="Delete venue"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        busy={Boolean(deletingId)}
        testId="school-venue-delete-confirm"
      />
    </ReadableCard>
  );
}

function ClassCatalogForm({
  initialClass,
  venues,
  instructorOptions,
  defaultInstructorId,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  onDelete,
}) {
  const [draft, setDraft] = useState(() => {
    const schedules = initialClass?.schedules?.length
      ? initialClass.schedules.map((schedule) => ({
          day: schedule.day,
          time: schedule.time,
          duration_minutes: schedule.duration_minutes || 60,
          venue_id: schedule.venue_id || VENUE_NONE,
        }))
      : (initialClass?.recurrence?.days || ["FRI"]).map((day) => ({
          day,
          time: initialClass?.recurrence?.time || "19:00",
          duration_minutes: initialClass?.recurrence?.duration_minutes || 60,
          venue_id: initialClass?.venue_id || venues[0]?.id || VENUE_NONE,
        }));
    return {
      name: initialClass?.name || "",
      description: initialClass?.description || "",
      whatsapp_url: initialClass?.whatsapp_url || "",
      cover_color: initialClass?.cover_color || "pink",
      schedules,
      instructorId: initialClass?.instructor_member_ids?.[0] || defaultInstructorId || "",
    };
  });
  const [errors, setErrors] = useState({});

  function update(patch) {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      if ("name" in patch) delete next.name;
      if ("schedules" in patch) delete next.schedules;
      return next;
    });
  }

  function toggleDay(day) {
    const exists = draft.schedules.some((schedule) => schedule.day === day);
    update({
      schedules: exists
        ? draft.schedules.filter((schedule) => schedule.day !== day)
        : [...draft.schedules, {
            day,
            time: draft.schedules[0]?.time || "19:00",
            duration_minutes: draft.schedules[0]?.duration_minutes || 60,
            venue_id: draft.schedules[0]?.venue_id || venues[0]?.id || VENUE_NONE,
          }],
    });
  }

  function updateSchedule(day, patch) {
    update({
      schedules: draft.schedules.map((schedule) =>
        schedule.day === day ? { ...schedule, ...patch } : schedule
      ),
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = {};
    if (!draft.name.trim()) nextErrors.name = "Enter a class name.";
    if (!draft.schedules.length) nextErrors.schedules = "Pick at least one day.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSubmit({
      name: draft.name.trim(),
      description: draft.description.trim(),
      whatsapp_url: draft.whatsapp_url.trim() || null,
      cover_color: draft.cover_color || "pink",
      schedules: draft.schedules.map((schedule) => ({
        ...schedule,
        venue_id: venueIdFromSelect(schedule.venue_id),
      })),
      instructor_member_ids: draft.instructorId ? [draft.instructorId] : [],
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="block">
        <FormLabel className="block mb-2">Class name *</FormLabel>
        <input
          className={fieldClass(errors.name)}
          value={draft.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Friday beginners"
          autoFocus={Boolean(initialClass)}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? "school-class-name-error" : undefined}
          data-testid="school-class-name"
        />
        {errors.name ? (
          <FormFieldError id="school-class-name-error">{errors.name}</FormFieldError>
        ) : null}
      </label>
      <label className="block">
        <FormLabel className="block mb-2">Description</FormLabel>
        <textarea
          className="field min-h-[72px]"
          value={draft.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="Optional — level, focus, what to bring"
          data-testid="school-class-description"
        />
      </label>
      <div>
        <FormLabel className="block mb-2">Cover colour</FormLabel>
        <CoverColorPicker
          value={draft.cover_color}
          onChange={(id) => update({ cover_color: id })}
          hint="Pick a colour so this class stands out on its page — useful when you run several regulars."
        />
      </div>
      <label className="block">
        <FormLabel className="block mb-2">Class WhatsApp group</FormLabel>
        <input
          className="field"
          value={draft.whatsapp_url}
          onChange={(e) => update({ whatsapp_url: e.target.value })}
          placeholder="https://chat.whatsapp.com/… (optional)"
          data-testid="school-class-whatsapp"
        />
        <FieldHint className="mt-1.5">
          Shown after students join. Falls back to the school community link if empty.
        </FieldHint>
      </label>
      <label className="block">
        <FormLabel className="block mb-2">Instructor</FormLabel>
        <select
          className="field"
          value={draft.instructorId}
          onChange={(e) => update({ instructorId: e.target.value })}
          data-testid="school-class-instructor"
        >
          <option value="">School host (default)</option>
          {instructorOptions.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name}
              {member.role === "ADMIN" ? " (admin)" : member.role === "INSTRUCTOR" ? " (instructor)" : ""}
            </option>
          ))}
        </select>
        <FieldHint className="mt-1.5">
          Optional — defaults to the school host. Add instructors on the People tab.
        </FieldHint>
      </label>
      <div>
        <FormLabel className="block mb-2">Days *</FormLabel>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((day) => {
            const active = draft.schedules.some((schedule) => schedule.day === day.k);
            return (
              <button
                key={day.k}
                type="button"
                onClick={() => toggleDay(day.k)}
                className={dayToggleClass(active)}
                aria-pressed={active}
                data-testid={`school-day-${day.k}`}
              >
                {active && (
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" strokeWidth={3} aria-hidden />
                )}
                {day.label}
              </button>
            );
          })}
        </div>
        {errors.schedules ? (
          <FormFieldError id="school-class-days-error">{errors.schedules}</FormFieldError>
        ) : null}
      </div>
      {draft.schedules.map((schedule) => (
        <div
          key={schedule.day}
          className="space-y-3 rounded-md2 bg-black/[0.03] p-3"
          data-testid={`school-day-schedule-${schedule.day}`}
        >
          <FormLabel className="font-semibold">
            {DAYS.find((day) => day.k === schedule.day)?.label || schedule.day}
          </FormLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label>
              <FormLabel className="block mb-2">Start time *</FormLabel>
              <input
                type="time"
                className="field"
                value={schedule.time}
                onChange={(e) => updateSchedule(schedule.day, { time: e.target.value })}
                data-testid="school-class-time"
              />
            </label>
            <DurationField
              value={schedule.duration_minutes}
              startTime={schedule.time}
              onChange={(value) => updateSchedule(schedule.day, { duration_minutes: value })}
              testId="school-class-duration"
            />
          </div>
          <VenueInput
            mode="school"
            venues={venues}
            venueId={schedule.venue_id}
            onVenueIdChange={(value) => updateSchedule(schedule.day, { venue_id: value })}
          />
        </div>
      ))}
      <div className={`flex flex-wrap items-center gap-2 pt-1 ${onDelete ? "justify-between" : "justify-end"}`}>
        {onDelete && (
          <ReadableNavButton
            type="button"
            variant="dangerOutline"
            onClick={onDelete}
            className="inline-flex items-center gap-1.5"
          >
            <Trash2 size={15} aria-hidden /> Delete class
          </ReadableNavButton>
        )}
        <div className="flex gap-2">
          <ReadableNavButton type="button" onClick={onCancel}>Cancel</ReadableNavButton>
          <ReadableNavButton type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving…" : submitLabel}
          </ReadableNavButton>
        </div>
      </div>
    </form>
  );
}

function ClassesTab({ school, classes, venues, team = [], calendar, onCreated }) {
  const instructorOptions = (team || []).filter((m) => m.status === "ACTIVE");
  const defaultInstructorId =
    instructorOptions.find((m) => m.role === "ADMIN")?.id ||
    instructorOptions[0]?.id ||
    "";

  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const formOpen = showForm || Boolean(editing);

  async function handleCreate(payload) {
    setBusy(true);
    try {
      await createClass(school.id, payload);
      toast.success("Class created");
      setShowForm(false);
      await onCreated();
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(cls) {
    setShowForm(false);
    setEditing(cls);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function handleUpdate(payload) {
    setBusy(true);
    try {
      await updateClass(school.id, editing.id, payload);
      toast.success("Class updated");
      setEditing(null);
      await onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteClass(school.id, deleteTarget.id);
      toast.success("Class deleted");
      setDeleteTarget(null);
      setEditing(null);
      await onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <ReadableCard className={FORM_STYLE_CARD_CLASS}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <SectionTitle>Classes</SectionTitle>
            <FieldHint>
              Weekly classes create upcoming sessions automatically. Classes need a venue from the Venues tab.
            </FieldHint>
          </div>
          {!formOpen && (
            <ReadableNavButton
              type="button"
              variant="primary"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              data-testid="school-add-class"
              aria-label="Add class"
            >
              <Plus size={16} strokeWidth={2.5} aria-hidden />
              Class
            </ReadableNavButton>
          )}
        </div>

        {formOpen && (
          <div className="pt-4 mt-2 border-t border-black/[0.08]">
            <ClassCatalogForm
              key={editing?.id || "new"}
              initialClass={editing || undefined}
              venues={venues}
              instructorOptions={instructorOptions}
              defaultInstructorId={defaultInstructorId}
              busy={busy}
              submitLabel={editing ? "Save changes" : "Create class"}
              onSubmit={editing ? handleUpdate : handleCreate}
              onCancel={closeForm}
              onDelete={editing ? () => setDeleteTarget(editing) : undefined}
            />
          </div>
        )}

        {classes.length ? (
          <ul className="pt-2 divide-y divide-black/[0.08]">
            {classes.map((cls) => (
              <li key={cls.id} className="py-3 first:pt-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/classes/${cls.id}/dashboard`}
                      className="hover:underline"
                      data-testid={`school-class-manage-${cls.id}`}
                    >
                      <div className="font-ui font-semibold text-[14px]">{cls.name}</div>
                      <div className="text-[13px] text-ink/70 mt-0.5">{formatRecurrence(cls.recurrence)}</div>
                    </Link>
                    {cls.description && (
                      <p className="text-[13px] text-ink/70 mt-1 line-clamp-2 whitespace-pre-line">
                        <LinkifiedText>{cls.description}</LinkifiedText>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link
                      to={`/classes/${cls.id}/dashboard`}
                      className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-plum"
                      data-testid={`school-class-dashboard-${cls.id}`}
                    >
                      Manage
                    </Link>
                    <button
                      type="button"
                      onClick={() => startEdit(cls)}
                      className="inline-flex min-h-[44px] items-center gap-1.5 px-1 text-sm font-semibold text-plum"
                      data-testid={`school-class-edit-${cls.id}`}
                    >
                      <Pencil size={14} aria-hidden /> Edit
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          !formOpen && <FieldHint className="pt-2">No classes yet. Tap + Class to add your first weekly class.</FieldHint>
        )}
        <DangerConfirmModal
          open={Boolean(deleteTarget)}
          title="Delete class?"
          message="This permanently deletes the class and its scheduled sessions. This can’t be undone."
          confirmLabel="Delete class"
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          busy={busy}
          testId="school-class-delete-confirm"
        />
      </ReadableCard>

      {calendar.instances?.length > 0 && (
        <ReadableCard className={FORM_STYLE_CARD_CLASS}>
          <SectionTitle>Upcoming sessions</SectionTitle>
          <ul className="divide-y divide-black/[0.08]">
            {calendar.instances.slice(0, 6).map((inst) => {
              const classId = inst.class_id || inst.class?.id;
              const instId = inst.id || inst._id;
              return (
                <li key={instId} className="py-3 text-sm flex justify-between gap-3 first:pt-1">
                  <Link
                    to={`/classes/${classId}/dashboard?instance=${encodeURIComponent(instId)}`}
                    className="font-medium text-ink hover:underline"
                    data-testid={`school-session-dashboard-${instId}`}
                  >
                    {inst.class?.name || "Class"}
                  </Link>
                  <span className="text-ink/70">{formatSessionWhen(inst)}</span>
                </li>
              );
            })}
          </ul>
        </ReadableCard>
      )}
    </div>
  );
}

function ProductCatalogForm({
  initialProduct,
  classes,
  currency,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  onDelete,
}) {
  const [draft, setDraft] = useState(() => ({
    name: initialProduct?.name || "Drop-in",
    type: initialProduct?.type || "DROPIN",
    offerType: initialProduct?.offer_type || "FIRST_CLASS_FREE",
    price: formatAmountWithCurrency(initialProduct?.price, currency),
    credits: initialProduct?.credits == null ? "" : String(initialProduct.credits),
    validityDays: initialProduct?.validity_days == null ? "" : String(initialProduct.validity_days),
    billingMode: initialProduct?.billing_mode || "rolling_days",
    scopeKind: initialProduct?.scope?.kind === "CLASS_IDS" ? "CLASS_IDS" : "SCHOOL_WIDE",
    scopedClassIds: initialProduct?.scope?.kind === "CLASS_IDS"
      ? initialProduct.scope.ids || []
      : [],
  }));
  const [errors, setErrors] = useState({});
  const isOffer = draft.type === "OFFER";

  function update(patch) {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach((key) => {
        if (key === "scopedClassIds" || key === "scopeKind") delete next.scope;
        else delete next[key];
      });
      return next;
    });
  }

  function selectType(nextType) {
    const currentTypeLabel = PRODUCT_TYPE_LABELS[draft.type] || "";
    const nextTypeLabel = PRODUCT_TYPE_LABELS[nextType] || "";
    update({
      type: nextType,
      name: !draft.name.trim() || draft.name.trim() === currentTypeLabel
        ? nextTypeLabel
        : draft.name,
      offerType: nextType === "OFFER" ? (draft.offerType || "FIRST_CLASS_FREE") : draft.offerType,
    });
  }

  function toggleScopedClass(id) {
    update({
      scopedClassIds: draft.scopedClassIds.includes(id)
        ? draft.scopedClassIds.filter((classId) => classId !== id)
        : [...draft.scopedClassIds, id],
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = {};
    if (!draft.name.trim()) nextErrors.name = "Enter a product name.";
    if (draft.scopeKind === "CLASS_IDS" && !draft.scopedClassIds.length) {
      nextErrors.scope = "Pick at least one class for this product.";
    }

    let parsedCurrency = currency || "EUR";
    if (!isOffer) {
      const parsedPrice = parseAmountWithCurrency(draft.price);
      if (!parsedPrice.ok) nextErrors.price = parsedPrice.error;
      else {
        parsedCurrency = parsedPrice.currency;
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (isOffer) {
      onSubmit({
        name: draft.name.trim(),
        type: "OFFER",
        offer_type: draft.offerType || "FIRST_CLASS_FREE",
        price: 0,
        currency: parsedCurrency,
        credits: null,
        validity_days: null,
        billing_mode: "rolling_days",
        scope: draft.scopeKind === "CLASS_IDS"
          ? { kind: "CLASS_IDS", ids: draft.scopedClassIds }
          : { kind: "SCHOOL_WIDE" },
      });
      return;
    }

    const parsedPrice = parseAmountWithCurrency(draft.price);
    onSubmit({
      name: draft.name.trim(),
      type: draft.type,
      offer_type: null,
      price: parsedPrice.amount,
      currency: parsedPrice.currency,
      credits: draft.type === "PACK" && draft.credits
        ? Number(draft.credits)
        : null,
      validity_days: draft.validityDays ? Number(draft.validityDays) : null,
      billing_mode: draft.type === "MEMBERSHIP" ? draft.billingMode : "rolling_days",
      scope: draft.scopeKind === "CLASS_IDS"
        ? { kind: "CLASS_IDS", ids: draft.scopedClassIds }
        : { kind: "SCHOOL_WIDE" },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <FormLabel className="block mb-2">Type *</FormLabel>
        <ChoiceToggleGroup
          options={PRODUCT_TYPES}
          value={draft.type}
          onChange={selectType}
        />
      </div>
      {isOffer && (
        <div>
          <FormLabel className="block mb-2">Offer Type *</FormLabel>
          <ChoiceToggleGroup
            options={OFFER_TYPE_OPTIONS.map((o) => ({ k: o.value, label: o.label }))}
            value={draft.offerType}
            onChange={(k) => update({ offerType: k })}
          />
        </div>
      )}
      <label className="block">
        <FormLabel className="block mb-2">Product name *</FormLabel>
        <input
          className={fieldClass(errors.name)}
          value={draft.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder={isOffer ? "First class free" : "10-class pack"}
          aria-invalid={Boolean(errors.name)}
          data-testid="school-product-name"
        />
        {errors.name ? <FormFieldError>{errors.name}</FormFieldError> : null}
      </label>
      {!isOffer && (
        <label className="block">
          <FormLabel className="block mb-2">Price *</FormLabel>
          <input
            className={fieldClass(errors.price)}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.price}
            onChange={(e) => update({ price: e.target.value })}
            placeholder={currency ? `15.0 ${currency}` : "15.0 EUR"}
            aria-invalid={Boolean(errors.price)}
            data-testid="school-product-price"
          />
          {errors.price ? <FormFieldError>{errors.price}</FormFieldError> : null}
          <FieldHint className="mt-1.5">Include amount and currency, e.g. 15.0 EUR.</FieldHint>
        </label>
      )}
      <div>
        <FormLabel className="block mb-2">Applies to *</FormLabel>
        <div className="mb-2">
          <ChoiceToggleGroup
            options={[
              { k: "SCHOOL_WIDE", label: "All classes" },
              { k: "CLASS_IDS", label: "Specific classes" },
            ]}
            value={draft.scopeKind}
            onChange={(k) => update({ scopeKind: k })}
          />
        </div>
        {draft.scopeKind === "CLASS_IDS" && (
          classes.length ? (
            <div className="space-y-2 rounded-md2 bg-black/[0.03] p-3">
              {classes.map((cls) => (
                <label key={cls.id} className="flex min-h-[36px] items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.scopedClassIds.includes(cls.id)}
                    onChange={() => toggleScopedClass(cls.id)}
                  />
                  {cls.name}
                </label>
              ))}
            </div>
          ) : (
            <FieldHint>Create classes first, then limit this product to specific ones.</FieldHint>
          )
        )}
        {errors.scope ? <FormFieldError>{errors.scope}</FormFieldError> : null}
      </div>
      {draft.type === "PACK" && (
        <label className="block">
          <FormLabel className="block mb-2">Credits</FormLabel>
          <input
            className="field"
            type="number"
            min="1"
            value={draft.credits}
            onChange={(e) => update({ credits: e.target.value })}
            placeholder="10"
          />
        </label>
      )}
      {draft.type === "MEMBERSHIP" && (
        <div>
          <FormLabel className="block mb-2">Billing</FormLabel>
          <ChoiceToggleGroup
            options={[
              { k: "calendar_month", label: "Calendar month (1st–1st)" },
              { k: "rolling_days", label: "Rolling days" },
            ]}
            value={draft.billingMode}
            onChange={(k) => update({ billingMode: k })}
          />
        </div>
      )}
      {(draft.type === "PACK" ||
        (draft.type === "MEMBERSHIP" && draft.billingMode === "rolling_days")) && (
        <label className="block">
          <FormLabel className="block mb-2">Validity (days)</FormLabel>
          <input
            className="field"
            type="number"
            min="1"
            value={draft.validityDays}
            onChange={(e) => update({ validityDays: e.target.value })}
            placeholder={draft.type === "PACK" ? "90" : "30"}
          />
        </label>
      )}
      <div className={`flex flex-wrap items-center gap-2 pt-1 ${onDelete ? "justify-between" : "justify-end"}`}>
        {onDelete && (
          <ReadableNavButton
            type="button"
            variant="dangerOutline"
            onClick={onDelete}
            className="inline-flex items-center gap-1.5"
          >
            <Trash2 size={15} aria-hidden /> Delete product
          </ReadableNavButton>
        )}
        <div className="flex gap-2">
          <ReadableNavButton type="button" onClick={onCancel}>Cancel</ReadableNavButton>
          <ReadableNavButton type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving…" : submitLabel}
          </ReadableNavButton>
        </div>
      </div>
    </form>
  );
}

function ProductsTab({ school, products, classes = [], onCreated, onSchoolUpdated }) {
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [commerceBusy, setCommerceBusy] = useState(false);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const formOpen = showForm || Boolean(editing);
  const commerceOn = school.commerce_enabled !== false;
  const allowCancel = Boolean(school.allow_rsvp_cancellation);
  const cancelDeadline = school.cancellation_deadline || "ANYTIME";

  async function toggleCommerce(enabled) {
    setCommerceBusy(true);
    try {
      await updateSchool(school.id, { commerce_enabled: enabled });
      toast.success(enabled ? "In-app payment tracking on" : "Collecting payments outside the app");
      await onSchoolUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommerceBusy(false);
    }
  }

  async function saveCancellationPolicy(patch) {
    setPolicyBusy(true);
    try {
      await updateSchool(school.id, patch);
      toast.success("Cancellation policy saved");
      await onSchoolUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPolicyBusy(false);
    }
  }

  async function handleCreate(payload) {
    setBusy(true);
    try {
      await createProduct(school.id, payload);
      toast.success("Product created");
      setShowForm(false);
      await onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  function scopeLabel(p) {
    if (p.scope?.kind === "CLASS_IDS") {
      const n = (p.scope.ids || []).length;
      return n ? `${n} class${n === 1 ? "" : "es"}` : "Specific classes";
    }
    return "All classes";
  }

  function startEdit(product) {
    setShowForm(false);
    setEditing(product);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function handleUpdate(payload) {
    setBusy(true);
    try {
      await updateProduct(school.id, editing.id, payload);
      toast.success("Product updated");
      setEditing(null);
      await onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteProduct(school.id, deleteTarget.id);
      toast.success("Product deleted");
      setDeleteTarget(null);
      setEditing(null);
      await onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <ReadableCard className={FORM_STYLE_CARD_CLASS}>
        <SectionTitle>Payment tracking</SectionTitle>
        <CheckboxField
          id="school-commerce-enabled"
          checked={commerceOn}
          onChange={(e) => toggleCommerce(e.target.checked)}
          title="Let students mark payments in the app"
          hint={
            commerceOn
              ? "Students can buy packs here and tap “Just paid.” Turn off if you collect money outside the app."
              : "You’re collecting money outside the app — here you’ll only track who attends class."
          }
          testId="school-commerce-toggle"
        />
        {commerceBusy && <FieldHint>Saving…</FieldHint>}
      </ReadableCard>

      <ReadableCard className={FORM_STYLE_CARD_CLASS}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <SectionTitle>Products</SectionTitle>
            <FieldHint>
              Drop-ins, packs, memberships, and offers students can use. Different prices? Make separate products and scope them to specific classes.
            </FieldHint>
          </div>
          {commerceOn && !formOpen && (
            <ReadableNavButton
              type="button"
              variant="primary"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              data-testid="school-add-product"
              aria-label="Add product"
            >
              <Plus size={16} strokeWidth={2.5} aria-hidden />
              Product
            </ReadableNavButton>
          )}
        </div>

        {commerceOn && (
          <p className="mt-3 font-ui text-[13px] text-ink/70 leading-snug">
            Payments use your{" "}
            <Link to="/me?tab=host&section=payment" className="font-semibold text-plum hover:underline">
              default payment method
            </Link>
            . Per-school payment settings aren&apos;t in this version.
          </p>
        )}

        {commerceOn && formOpen && (
          <div className="pt-4 mt-2 border-t border-black/[0.08]">
            <ProductCatalogForm
              key={editing?.id || "new"}
              initialProduct={editing || undefined}
              classes={classes}
              currency={editing?.currency || school.currency || "EUR"}
              busy={busy}
              submitLabel={editing ? "Save changes" : "Create product"}
              onSubmit={editing ? handleUpdate : handleCreate}
              onCancel={closeForm}
              onDelete={editing ? () => setDeleteTarget(editing) : undefined}
            />
          </div>
        )}

        {products.length ? (
          <ul className="pt-2 divide-y divide-black/[0.08]">
            {products.map((p) => (
              <li key={p.id} className="py-3 first:pt-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-ui font-semibold text-[14px]">{p.name}</div>
                    <div className="text-[13px] text-ink/70 mt-0.5">
                      {PRODUCT_TYPE_LABELS[p.type] || p.type}
                      {p.type === "OFFER" && p.offer_type === "FIRST_CLASS_FREE"
                        ? " · First Class Free"
                        : ""}
                      {p.credits != null ? ` · ${p.credits} credits` : ""}
                      {p.billing_mode === "calendar_month"
                        ? " · calendar month"
                        : p.validity_days != null
                          ? ` · ${p.validity_days} days`
                          : ""}
                      {` · ${scopeLabel(p)}`}
                    </div>
                    <div className="font-medium mt-1">
                      {p.type === "OFFER" ? "Free" : `${p.price} ${p.currency || school.currency || "EUR"}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => startEdit(p)}
                    className="inline-flex min-h-[44px] items-center gap-1.5 px-1 font-semibold text-plum shrink-0"
                    data-testid={`school-product-edit-${p.id}`}
                  >
                    <Pencil size={14} aria-hidden /> Edit
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          commerceOn && !formOpen && (
            <FieldHint className="pt-2">No products yet. Tap + Product to add a drop-in or class pack.</FieldHint>
          )
        )}

        {!commerceOn && (
          <FieldHint className="pt-2">
            Catalog hidden while you collect money outside the app. Turn payment tracking back on above to sell packs here.
          </FieldHint>
        )}
        <DangerConfirmModal
          open={Boolean(deleteTarget)}
          title="Delete product?"
          message="This removes the product from your catalog. Existing payment and entitlement history will be kept."
          confirmLabel="Delete product"
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          busy={busy}
          testId="school-product-delete-confirm"
        />
      </ReadableCard>

      <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-cancellation-policy">
        <SectionTitle>RSVP Cancellation Policy</SectionTitle>
        <FieldHint>
          When enabled, students can cancel their own class RSVP. Late cancels on a First Class Free offer count as using the trial unless you forgive them from Action Items.
        </FieldHint>
        <div className="pt-3">
          <CheckboxField
            id="school-allow-rsvp-cancellation"
            checked={allowCancel}
            onChange={(e) => saveCancellationPolicy({ allow_rsvp_cancellation: e.target.checked })}
            title="Allow Cancelling RSVP"
            hint="Students see a Cancel option on their confirmed class RSVP."
            testId="school-allow-cancel-toggle"
          />
        </div>
        {allowCancel && (
          <label className="block pt-4">
            <FormLabel className="block mb-2">Cancellation deadline</FormLabel>
            <select
              className="field"
              value={cancelDeadline}
              disabled={policyBusy}
              onChange={(e) => saveCancellationPolicy({ cancellation_deadline: e.target.value })}
              data-testid="school-cancellation-deadline"
            >
              {CANCELLATION_DEADLINE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        )}
        {policyBusy && <FieldHint className="pt-2">Saving…</FieldHint>}
      </ReadableCard>
    </div>
  );
}

function ActionItemsTab({ school, people, onRefreshPeople, onPendingCountChange }) {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [rsvpItems, setRsvpItems] = useState([]);
  const [doorItems, setDoorItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [waPrompt, setWaPrompt] = useState(null);
  const [askFeeling, setAskFeeling] = useState(true);
  const [followUp, setFollowUp] = useState(null);
  const [detailPerson, setDetailPerson] = useState(null);
  const [reviewItem, setReviewItem] = useState(null);
  const [doorConfirm, setDoorConfirm] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [p, a, r, actionData, doorData] = await Promise.all([
        fetchPaymentQueue(school.id, "PENDING"),
        fetchPaymentQueue(school.id, "APPROVED"),
        fetchPaymentQueue(school.id, "REJECTED"),
        fetchSchoolActionItems(school.id, "OPEN").catch(() => ({ action_items: [], open_count: 0 })),
        fetchDoorQueue(school.id).catch(() => ({ items: [], door_count: 0 })),
      ]);
      setPending(p || []);
      setApproved((a || []).slice(0, 20));
      setRejected((r || []).slice(0, 20));
      setRsvpItems(actionData?.action_items || []);
      setDoorItems(doorData?.items || []);
      onPendingCountChange?.(
        (p || []).length
          + Number(actionData?.open_count || 0)
          + Number(doorData?.door_count || (doorData?.items || []).length || 0),
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [school.id]);

  function personFor(organizerId) {
    const fromPeople = (people?.people || []).find((x) => x.id === organizerId);
    if (fromPeople) return { ...fromPeople, isPerson: true };
    const fromTeam = (people?.team || []).find((x) => x.organizer_id === organizerId);
    if (fromTeam) {
      return {
        id: fromTeam.organizer_id,
        name: fromTeam.name,
        phone: fromTeam.phone,
        isPerson: false,
        membership: { id: fromTeam.id, role: fromTeam.role },
        activity: fromTeam.activity,
        activity_label: fromTeam.activity_label,
        note: fromTeam.note,
      };
    }
    return { id: organizerId, name: "Student", phone: null, isPerson: true };
  }

  function openPerson(organizerId, fallbackName, fallbackPhone) {
    if (!organizerId) return;
    const base = personFor(organizerId);
    setDetailPerson({
      ...base,
      name: base.name && base.name !== "Student" ? base.name : (fallbackName || base.name),
      phone: base.phone || fallbackPhone || null,
    });
  }

  async function decide(payment, approve) {
    setBusyId(payment.id);
    try {
      const result = await reviewPayment(payment.id, { approve });
      toast.success(approve ? "Payment approved" : "Payment rejected");
      await load();
      onRefreshPeople?.();
      const person = personFor(payment.organizer_id);
      const displayName = (payment.guest_name || "").trim()
        || (person.name && person.name !== "Student" ? person.name : null)
        || "Student";
      const phone = result?.whatsapp_hint?.phone || payment.guest_phone || person.phone;
      if (approve && result?.whatsapp_hint) {
        setAskFeeling(true);
        setWaPrompt({
          isNewStudent: Boolean(result.whatsapp_hint.is_new_student),
          name: result.whatsapp_hint.name || displayName,
          phone,
        });
      } else if (!approve) {
        setFollowUp({
          title: "Payment rejected",
          description: `Optionally tell ${displayName} about the rejection.`,
          phone,
          message: buildFeeRejectionMessage({
            studentName: displayName,
            schoolName: school.name,
          }),
          testId: "fee-reject-whatsapp",
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function resolveReview(resolution) {
    if (!reviewItem) return;
    setBusyId(reviewItem.id);
    try {
      await resolveSchoolActionItem(school.id, reviewItem.id, resolution);
      toast.success(resolution === "FORGIVE" ? "Offer reset for student" : "Offer marked as used");
      const item = reviewItem;
      setReviewItem(null);
      await load();
      onRefreshPeople?.();
      setFollowUp({
        title: resolution === "FORGIVE" ? "Offer reset" : "Offer marked used",
        description: `Send ${item.student_name || "the student"} a quick update.`,
        phone: item.student_phone,
        message: buildLateCancelResolutionWhatsAppMessage({
          studentName: item.student_name,
          className: item.class_name,
          schoolName: school.name,
          forgiven: resolution === "FORGIVE",
        }),
        testId: "late-cancel-whatsapp",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmAttendance() {
    if (!doorConfirm) return;
    const item = doorConfirm.item;
    setBusyId(item.id);
    try {
      const result = await markInstanceAttendance(item.class_instance_id, item.organizer_id);
      const impact = result?.credit_impact;
      toast.success(
        impact?.will_debit_credits
          ? `Checked in — 1 credit used (${impact.credits_remaining_after ?? "?"} left)`
          : "Checked in",
      );
      setDoorConfirm(null);
      await load();
      onRefreshPeople?.();
      setFollowUp({
        title: "Checked in",
        description: `Optionally confirm with ${item.student_name || "the student"} on WhatsApp.`,
        phone: item.student_phone,
        message: buildAttendanceWhatsAppMessage({
          studentName: item.student_name,
          className: item.class_name,
          schoolName: school.name,
        }),
        testId: "door-attendance-whatsapp",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmNoShow(resolution) {
    if (!doorConfirm) return;
    const item = doorConfirm.item;
    setBusyId(item.id);
    try {
      const result = await markInstanceNoShow(item.class_instance_id, item.organizer_id, resolution);
      const impact = result?.credit_impact;
      toast.success(
        resolution === "DEDUCT" && impact?.will_debit_credits
          ? `Marked no-show — 1 credit deducted (${impact.credits_remaining_after ?? "?"} left)`
          : "Marked no-show",
      );
      setDoorConfirm(null);
      await load();
      onRefreshPeople?.();
      setFollowUp({
        title: "No-show marked",
        description: `Optionally message ${item.student_name || "the student"}.`,
        phone: item.student_phone,
        message: buildNoShowWhatsAppMessage({
          studentName: item.student_name,
          className: item.class_name,
          schoolName: school.name,
          deducted: resolution === "DEDUCT",
        }),
        testId: "door-noshow-whatsapp",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  const waMessage = waPrompt
    ? buildFeeApprovalMessage({
      schoolName: school.name,
      studentName: waPrompt.name,
      isNewStudent: waPrompt.isNewStudent,
      askFeeling: !waPrompt.isNewStudent && askFeeling,
    })
    : "";

  const hasPayments = pending.length > 0;
  const hasDoor = doorItems.length > 0;
  const hasLateCancels = rsvpItems.length > 0;
  const queuesClear = !hasPayments && !hasDoor && !hasLateCancels;

  return (
    <div className="space-y-6" data-testid="school-action-items-tab">
      {loading ? (
        <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-action-items-loading">
          <div className="animate-pulse motion-reduce:animate-none space-y-3" aria-busy="true" aria-label="Loading action items">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 rounded-md2 bg-black/[0.06]" />
            ))}
            <p className="sr-only">Loading action items…</p>
          </div>
        </ReadableCard>
      ) : queuesClear ? (
        <EmptyState
          compact
          className={FORM_STYLE_CARD_CLASS}
          title="All caught up!"
          description="No pending action items."
          data-testid="school-action-items-caught-up"
        />
      ) : (
        <>
          {hasPayments ? (
            <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-fees-tab">
              <ActionQueueHeading
                title="Payments"
                count={pending.length}
                help="Approve when a student has paid, or follow up if they RSVP’d without paying. Tap a name to adjust credits."
                helpTestId="school-fees-help"
              />
              <ul className="mt-2 divide-y divide-black/[0.08]">
                {pending.map((payment) => {
                  const person = personFor(payment.organizer_id);
                  const displayName = (payment.guest_name || "").trim()
                    || (person.name && person.name !== "Student" ? person.name : null)
                    || "Student";
                  const unpaid = payment.amount_paid == null;
                  const amountMismatch = !unpaid
                    && Number(payment.amount_paid) !== Number(payment.listed_price);
                  const contactPhone = payment.guest_phone || person.phone;
                  const waHref = phoneToWaMe(contactPhone);
                  return (
                    <li
                      key={payment.id}
                      className={`py-3 space-y-2 ${unpaid ? "rounded-md2 bg-amber-50/80 px-3 -mx-1" : ""}`}
                      data-testid={`fee-pending-${payment.id}`}
                      data-unpaid={unpaid ? "true" : undefined}
                    >
                      <div className="min-w-0">
                        <button
                          type="button"
                          className="font-ui font-semibold text-[15px] text-left min-h-[44px] -my-1 py-1 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm"
                          onClick={() => openPerson(
                            payment.organizer_id,
                            payment.guest_name,
                            payment.guest_phone,
                          )}
                          data-testid={`fee-person-${payment.id}`}
                        >
                          {displayName}
                        </button>
                        {unpaid ? (
                          <p className="text-[13px] font-medium text-ink/90" data-testid={`fee-unpaid-${payment.id}`}>
                            Hasn’t paid yet · listed {payment.listed_price}{" "}
                            {payment.currency || school.currency || "EUR"}
                          </p>
                        ) : (
                          <p className="text-[13px] text-ink/70">
                            Listed {payment.listed_price} {payment.currency || school.currency || "EUR"}
                            {amountMismatch ? ` · said paid ${payment.amount_paid}` : ""}
                            {" · "}{payment.method}
                          </p>
                        )}
                        {payment.note ? (
                          <p className="text-[13px] text-ink/70 mt-1">Note: {payment.note}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {waHref ? (
                          <ReadableNavButton
                            type="button"
                            onClick={() => {
                              const ok = openFeeWhatsApp({
                                phone: contactPhone,
                                message: buildFeeChaseMessage({
                                  studentName: displayName,
                                  schoolName: school.name,
                                }),
                              });
                              if (!ok) toast.error("Could not open WhatsApp");
                            }}
                            data-testid={`fee-whatsapp-${payment.id}`}
                          >
                            WhatsApp
                          </ReadableNavButton>
                        ) : null}
                        <ReadableNavButton
                          type="button"
                          variant="primary"
                          disabled={busyId === payment.id}
                          onClick={() => decide(payment, true)}
                          data-testid={`fee-approve-${payment.id}`}
                        >
                          Approve
                        </ReadableNavButton>
                        <ReadableNavButton
                          type="button"
                          variant="dangerOutline"
                          disabled={busyId === payment.id}
                          onClick={() => decide(payment, false)}
                          data-testid={`fee-reject-${payment.id}`}
                        >
                          Reject
                        </ReadableNavButton>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </ReadableCard>
          ) : null}

          {hasDoor ? (
            <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-door-queue">
              <ActionQueueHeading
                title="Live Check-In"
                count={doorItems.length}
                help="RSVP’d students within the active check-in window (from 1 hour before class). Mark attendance or no-show — credits adjust when you confirm."
                helpTestId="school-door-help"
              />
              <ul className="mt-2 divide-y divide-black/[0.08]">
                {doorItems.map((item) => (
                  <li key={item.id} className="py-3 space-y-2" data-testid={`door-item-${item.id}`}>
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="font-ui font-semibold text-[15px] text-left min-h-[44px] -my-1 py-1 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm"
                        onClick={() => openPerson(item.organizer_id, item.student_name, item.student_phone)}
                        data-testid={`door-person-${item.id}`}
                      >
                        {item.student_name || "Student"}
                      </button>
                      <p className="text-[13px] text-ink/70">
                        {item.class_name || "Class"}
                        {item.session_datetime ? ` (${formatDt(item.session_datetime)})` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ReadableNavButton
                        type="button"
                        variant="primary"
                        disabled={busyId === item.id}
                        onClick={() => setDoorConfirm({ mode: "attendance", item })}
                        data-testid={`door-attend-${item.id}`}
                      >
                        Mark attendance
                      </ReadableNavButton>
                      <ReadableNavButton
                        type="button"
                        disabled={busyId === item.id}
                        onClick={() => setDoorConfirm({ mode: "no-show", item })}
                        data-testid={`door-noshow-${item.id}`}
                      >
                        No show
                      </ReadableNavButton>
                    </div>
                  </li>
                ))}
              </ul>
            </ReadableCard>
          ) : null}

          {hasLateCancels ? (
            <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-rsvp-action-list">
              <ActionQueueHeading
                title="Late cancellations"
                count={rsvpItems.length}
                help="Students who cancelled their RSVP after the school’s late cancellation policy window."
                helpTestId="school-rsvp-help"
              />
              <ul className="mt-2 divide-y divide-black/[0.08]">
                {rsvpItems.map((item) => (
                  <li key={item.id} className="py-3 space-y-2" data-testid={`rsvp-action-${item.id}`}>
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="font-ui font-semibold text-[15px] text-left min-h-[44px] -my-1 py-1 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm"
                        onClick={() => openPerson(item.organizer_id, item.student_name, item.student_phone)}
                        data-testid={`rsvp-action-person-${item.id}`}
                      >
                        {item.student_name || "Student"}
                      </button>
                      <p className="text-[13px] text-ink/70">
                        {item.class_name || "Class"}
                        {item.session_datetime ? ` · ${formatDt(item.session_datetime)}` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ReadableNavButton
                        type="button"
                        variant="primary"
                        disabled={busyId === item.id}
                        onClick={() => setReviewItem(item)}
                        data-testid={`rsvp-action-review-${item.id}`}
                      >
                        Review
                      </ReadableNavButton>
                      {item.student_phone && (
                        <ReadableNavButton
                          type="button"
                          onClick={() => {
                            const ok = openFeeWhatsApp({
                              phone: item.student_phone,
                              message: `Hi ${item.student_name || "there"} — checking in about your class cancellation at ${school.name}.`,
                            });
                            if (!ok) toast.error("Could not open WhatsApp");
                          }}
                        >
                          WhatsApp
                        </ReadableNavButton>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </ReadableCard>
          ) : null}
        </>
      )}

      <ReadableCard className={FORM_STYLE_CARD_CLASS} data-testid="school-fee-history">
        <button
          type="button"
          className="w-full flex items-center justify-between gap-3 min-h-[44px] text-left"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-expanded={historyOpen}
          data-testid="school-fee-history-toggle"
        >
          <h3 className="font-ui font-semibold text-[16px]">Recent payment history</h3>
          <span className="text-[13px] text-ink/60">{historyOpen ? "Hide" : "Show"}</span>
        </button>
        {historyOpen && !loading ? (
          <div className="pt-2 space-y-4">
            <div>
              <h4 className="font-ui font-semibold text-[15px]">Recently approved</h4>
              {approved.length === 0 ? (
                <FieldHint className="mt-2">No approved payments yet.</FieldHint>
              ) : (
                <ul className="mt-1 divide-y divide-black/[0.08]">
                  {approved.map((payment) => {
                    const person = personFor(payment.organizer_id);
                    return (
                      <li key={payment.id} className="text-sm flex justify-between gap-2 py-2.5 items-center">
                        <button
                          type="button"
                          className="min-w-0 text-left min-h-[44px] py-1 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm"
                          onClick={() => openPerson(payment.organizer_id)}
                        >
                          <span className="font-medium">{person.name}</span>
                          {payment.note ? <span className="text-ink/60"> · {payment.note}</span> : null}
                        </button>
                        <span className="shrink-0 text-ink/70">
                          {payment.amount_paid} {payment.currency || school.currency || "EUR"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="pt-2 border-t border-black/[0.08]">
              <h4 className="font-ui font-semibold text-[15px]">Recently rejected</h4>
              {rejected.length === 0 ? (
                <FieldHint className="mt-2">No rejected payments yet.</FieldHint>
              ) : (
                <ul className="mt-1 divide-y divide-black/[0.08]">
                  {rejected.map((payment) => {
                    const person = personFor(payment.organizer_id);
                    return (
                      <li
                        key={payment.id}
                        className="text-sm flex justify-between gap-2 py-2.5 items-center"
                        data-testid={`fee-rejected-${payment.id}`}
                      >
                        <button
                          type="button"
                          className="min-w-0 text-left min-h-[44px] py-1 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm"
                          onClick={() => openPerson(payment.organizer_id)}
                        >
                          <span className="font-medium">{person.name}</span>
                          {payment.note ? <span className="text-ink/60"> · {payment.note}</span> : null}
                        </button>
                        <span className="shrink-0 text-ink/70">
                          {payment.listed_price} {payment.currency || school.currency || "EUR"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </ReadableCard>

      {detailPerson && (
        <PersonSheetHost
          schoolId={school.id}
          schoolName={school.name}
          person={detailPerson}
          onClose={() => setDetailPerson(null)}
          onRefresh={onRefreshPeople}
        />
      )}

      {reviewItem && (
        <AdminCancellationModal
          studentName={reviewItem.student_name || "Student"}
          busy={busyId === reviewItem.id}
          onClose={() => setReviewItem(null)}
          onResolve={resolveReview}
        />
      )}

      {doorConfirm && (
        <DoorAttendanceConfirmModal
          mode={doorConfirm.mode}
          studentName={doorConfirm.item.student_name || "Student"}
          className={doorConfirm.item.class_name || "Class"}
          creditImpact={doorConfirm.item.credit_impact}
          busy={busyId === doorConfirm.item.id}
          onClose={() => setDoorConfirm(null)}
          onConfirmAttendance={confirmAttendance}
          onConfirmNoShow={confirmNoShow}
        />
      )}

      {waPrompt && (
        <FeeWhatsAppPrompt
          waPrompt={waPrompt}
          waMessage={waMessage}
          askFeeling={askFeeling}
          setAskFeeling={setAskFeeling}
          onClose={() => setWaPrompt(null)}
        />
      )}

      {followUp ? (
        <WhatsAppFollowUpPrompt
          title={followUp.title}
          description={followUp.description}
          phone={followUp.phone}
          message={followUp.message}
          onClose={() => setFollowUp(null)}
          testId={followUp.testId}
        />
      ) : null}
    </div>
  );
}

function AdminCancellationModal({ studentName, busy, onClose, onResolve }) {
  const dialogRef = useRef(null);
  const [resolution, setResolution] = useState("FORFEIT");

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      onBackdropClose={onClose}
      testId="admin-cancellation-modal"
      titleId="admin-cancellation-title"
      layout="sheet"
    >
      <WorkflowModalHeader
        title={`Cancel RSVP for ${studentName}`}
        titleId="admin-cancellation-title"
        onClose={onClose}
        sticky
      />
      <WorkflowModalBody sheet className="space-y-4">
        <FieldHint>
          This cancellation request is outside the school&apos;s policy window. How would you like to handle this trial offer?
        </FieldHint>
        <div className="space-y-3">
          {[
            {
              value: "FORFEIT",
              label: "Charge / forfeit offer",
              hint: "Mark the First Class Free offer as used.",
            },
            {
              value: "FORGIVE",
              label: "Waive penalty / reset offer",
              hint: "Student can book another free class.",
            },
          ].map((option) => (
            <label
              key={option.value}
              className="flex gap-3 items-start rounded-md2 border border-black/[0.1] px-3 py-3 cursor-pointer"
            >
              <input
                type="radio"
                name="trial-resolution"
                className="mt-1"
                checked={resolution === option.value}
                onChange={() => setResolution(option.value)}
              />
              <span>
                <span className="font-ui font-semibold text-[14px] block">{option.label}</span>
                <span className="text-[13px] text-ink/70">{option.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </WorkflowModalBody>
      <WorkflowModalFooter sticky safeArea className="flex flex-wrap gap-2">
        <ReadableNavButton type="button" onClick={onClose}>Back</ReadableNavButton>
        <ReadableNavButton
          type="button"
          variant="primary"
          disabled={busy}
          onClick={() => onResolve(resolution)}
          data-testid="admin-cancel-confirm"
        >
          {busy ? "Saving…" : "Confirm"}
        </ReadableNavButton>
      </WorkflowModalFooter>
    </WorkflowModalShell>
  );
}

function FeeWhatsAppPrompt({ waPrompt, waMessage, askFeeling, setAskFeeling, onClose }) {
  const dialogRef = useRef(null);
  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      onBackdropClose={onClose}
      testId="fee-whatsapp-modal"
      titleId="fee-whatsapp-title"
      layout="sheet"
    >
      <WorkflowModalHeader
        title={waPrompt.isNewStudent ? "Welcome message" : "Renewal message"}
        titleId="fee-whatsapp-title"
        onClose={onClose}
        sticky
      />
      <WorkflowModalBody sheet className="space-y-3">
        <FieldHint>
          Send a WhatsApp note to {waPrompt.name || "the student"} after approving their payment.
        </FieldHint>
        {!waPrompt.isNewStudent && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={askFeeling}
              onChange={(e) => setAskFeeling(e.target.checked)}
              data-testid="fee-wa-feeling"
            />
            <span>Ask how they’re feeling about class</span>
          </label>
        )}
        <p className="rounded-md2 border border-black/[0.08] bg-black/[0.03] px-3 py-3 text-sm whitespace-pre-wrap">
          {waMessage}
        </p>
      </WorkflowModalBody>
      <WorkflowModalFooter sticky safeArea className="flex flex-wrap gap-2">
        <ReadableNavButton
          type="button"
          variant="primary"
          disabled={!phoneToWaMe(waPrompt.phone)}
          onClick={() => {
            const ok = openFeeWhatsApp({ phone: waPrompt.phone, message: waMessage });
            if (!ok) toast.error("No phone on file for WhatsApp");
            else onClose();
          }}
          data-testid="fee-wa-send"
        >
          Send on WhatsApp
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(waMessage).then(
              () => toast.success("Message copied"),
              () => toast.error("Could not copy"),
            );
          }}
        >
          Copy message
        </ReadableNavButton>
        <ReadableNavButton type="button" onClick={onClose}>
          Skip
        </ReadableNavButton>
      </WorkflowModalFooter>
    </WorkflowModalShell>
  );
}
